import { create } from 'zustand';
import { authService, User, LoginDto, RegisterDto } from '../services/authService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requires2FA: boolean;

  // Actions
  login: (data: LoginDto) => Promise<boolean>;
  register: (data: RegisterDto) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshUser: () => void;
  clearError: () => void;
  setRequires2FA: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: authService.getCurrentUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
  requires2FA: false,

  login: async (data: LoginDto) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(data);

      // Check if 2FA is required
      if ('requires2FA' in response && response.requires2FA) {
        set({ requires2FA: true, isLoading: false });
        return false; // Indicates 2FA is needed
      }

      // Successful login - response is AuthResponse at this point
      const authResponse = response as import('../services/authService').AuthResponse;
      set({
        user: authResponse.user,
        isAuthenticated: true,
        isLoading: false,
        requires2FA: false,
      });
      return true; // Indicates successful login
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Login failed. Please try again.',
        isLoading: false,
        requires2FA: false,
      });
      return false;
    }
  },

  register: async (data: RegisterDto) => {
    set({ isLoading: true, error: null });
    try {
      await authService.register(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Registration failed. Please try again.',
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requires2FA: false,
      });
    } catch (error: any) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requires2FA: false,
      });
    }
  },

  logoutAll: async () => {
    set({ isLoading: true });
    try {
      await authService.logoutAll();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requires2FA: false,
      });
    } catch (error: any) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requires2FA: false,
      });
    }
  },

  refreshUser: () => {
    const user = authService.getCurrentUser();
    const isAuthenticated = authService.isAuthenticated();
    set({ user, isAuthenticated });
  },

  clearError: () => set({ error: null }),

  setRequires2FA: (value: boolean) => set({ requires2FA: value }),
}));
